import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PRODUCTS } from '../data/farmerData';
import { WaveDivider, WaveRule, CardWaveCut, PALETTE, useReveal } from '../components/WaveTheme';
import gheeIndigenousMethodImg from '../image/ghee_indigenous_method.svg';
import './CustomerView.css';

const formatFeet = (ft) => `${ft.toLocaleString()} ft`;

const convertToEmbedUrl = (mapsUrl) => {
  if (!mapsUrl) return null;
  if (mapsUrl.includes('google.com/maps/d/')) {
    const midMatch = mapsUrl.match(/mid=([^&]+)/);
    if (midMatch) {
      return `https://www.google.com/maps/d/embed?mid=${midMatch[1]}&maptype=satellite`;
    }
  }
  if (mapsUrl.includes('/embed')) {
    return mapsUrl.includes('maptype=') ? mapsUrl : `${mapsUrl}${mapsUrl.includes('?') ? '&' : '?'}maptype=satellite`;
  }
  return mapsUrl;
};

const DEFAULT_SUPPLY_CHAIN = [
  { icon: '🏔️', title: 'Himalayan Cluster Village', desc: 'Elevation > 5,900 ft · Organic, traditional farming' },
  { icon: '☀️', title: 'Processing', desc: 'Sun drying, cleaning, sorting and packed in Bhangeli with Himalayan air inside' },
  { icon: '🚚', title: 'First Mile Logistics', desc: 'Courier partner / pickup hub — Uttarkashi' },
  { icon: '🏠', title: 'Last Mile Delivery', desc: 'Your home — pure, nutritious, traceable Himalayan produce' },
];

function MapEmbed({ label, url }) {
  const embedUrl = convertToEmbedUrl(url);
  return (
    <div className="wtn-farm">
      {label && <p className="wtn-farm__label">{label}</p>}
      {embedUrl && (
        <div className="wtn-map">
          <iframe
            src={embedUrl}
            className="wtn-map__iframe"
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={label || 'Map'}
          />
        </div>
      )}
      <a href={url} target="_blank" rel="noopener noreferrer" className="wtn-map__link">
        Open in Google Maps →
      </a>
    </div>
  );
}

function SectionHead({ eyebrow, title, sub }) {
  return (
    <div className="wtn-section__head wtn-reveal">
      {eyebrow && <span className="wtn-eyebrow">{eyebrow}</span>}
      {title && <h2 className="wtn-h2">{title}</h2>}
      <div className="wtn-h2-rule"><WaveRule /></div>
      {sub && <p className="wtn-section__sub">{sub}</p>}
    </div>
  );
}

function CustomerView() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState(0);

  const data = PRODUCTS[decodeURIComponent(batchId)];
  useReveal([batchId]);

  if (!data) {
    return (
      <div className="wtn-cv">
        <div className="wtn-notfound">
          <span className="wtn-eyebrow">Batch not found</span>
          <h2 className="wtn-h2">This code isn't in our records.</h2>
          <div className="wtn-h2-rule"><WaveRule /></div>
          <p className="wtn-notfound__body">
            We couldn't find a product with this batch ID. Check the code on your package and try again.
          </p>
          <button onClick={() => navigate('/')} className="wtn-btn wtn-btn--primary wtn-btn--large">
            ← Back
          </button>
        </div>
      </div>
    );
  }

  const { village, farmer, customSupplyChain } = data;
  const supplyChain = customSupplyChain || DEFAULT_SUPPLY_CHAIN;

  return (
    <div className="wtn-cv">

      {/* Sticky Header */}
      <header className="wtn-cv-header">
        <div className="wtn-cv-header__inner">
          <button onClick={() => navigate('/')} className="wtn-cv-header__back" aria-label="Back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="wtn-cv-header__content">
            <p className="wtn-cv-header__eyebrow">Batch · {data.batch_id}</p>
            <p className="wtn-cv-header__title">{data.product}</p>
          </div>
          <div className="wtn-cv-header__badge">OUO</div>
        </div>
      </header>

      {/* Farmer image hero — first thing the customer sees */}
      <section className="wtn-farmer-hero">
        <div className="wtn-farmer-hero__imgwrap">
          <img
            src={farmer.image}
            alt={farmer.name}
            className="wtn-farmer-hero__img"
            onError={(e) => { e.target.src = 'https://via.placeholder.com/1200x1400?text=Farmer+Photo'; }}
          />
          <div className="wtn-farmer-hero__overlay">
            <span className="wtn-eyebrow wtn-eyebrow--dark">Our Farmer</span>
            <h1 className="wtn-h1 wtn-h1--cream wtn-farmer-hero__name">{farmer.name}</h1>
            <p className="wtn-farmer-hero__loc">
              {village.name} · {village.district}, {village.state}
              {village.elevation_ft && <span> · {formatFeet(village.elevation_ft)}</span>}
            </p>
          </div>
          <CardWaveCut fill={PALETTE.CREAM_SOFT} />
        </div>
      </section>

      {/* Farmer bio */}
      <section className="wtn-section wtn-farmer">
        <div className="wtn-section__inner">
          <div className="wtn-farmer__body wtn-reveal">
            <div className="wtn-farmer__meta">
              {farmer.age && <span className="wtn-btag">Age {farmer.age}</span>}
              {farmer.gender && <span className="wtn-btag">{farmer.gender}</span>}
            </div>
            <p className="wtn-farmer__bio">{farmer.bio}</p>
          </div>
        </div>
      </section>

      <WaveDivider height={72} palette={[PALETTE.CREAM_SOFT, PALETTE.CREAM]} flip />

      {/* Village */}
      <section className="wtn-section wtn-village">
        <div className="wtn-section__inner">
          <SectionHead
            eyebrow="The Village"
            title={village.name}
            sub={`${village.district}, ${village.state}${village.elevation_ft ? ` · ${formatFeet(village.elevation_ft)} elevation` : ''}`}
          />
          <div className="wtn-village__body wtn-reveal">
            <p>{village.description}</p>
          </div>
        </div>
      </section>

      {/* Indigenous Method — Ghee only */}
      {farmer.indigenousMethod && (
        <>
          <WaveDivider height={72} palette={[PALETTE.CREAM_SOFT]} />
          <section className="wtn-section wtn-indigenous">
            <div className="wtn-section__inner">
              <SectionHead
                eyebrow="Indigenous Method"
                title={farmer.indigenousMethod.heading}
                sub={farmer.indigenousMethod.subheading}
              />
              <div className="wtn-indigenous__image wtn-reveal">
                <img
                  src={gheeIndigenousMethodImg}
                  alt="Badri cow ghee indigenous production method — bilona process illustration"
                />
              </div>
              {farmer.indigenousMethod.ghResidueNote && (
                <div className="wtn-gr wtn-reveal">
                  <span className="wtn-gr__badge">GR</span>
                  <p className="wtn-gr__text">{farmer.indigenousMethod.ghResidueNote}</p>
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* Breed Info — Ghee only */}
      {farmer.breedInfo && (
        <>
          <WaveDivider height={72} palette={[PALETTE.CREAM, PALETTE.CREAM_SOFT]} flip />
          <section className="wtn-section wtn-breed">
            <div className="wtn-section__inner">
              <SectionHead
                eyebrow="The Breed"
                title={farmer.breedInfo.title}
              />
              <div className="wtn-breed__body wtn-reveal-group">
                {farmer.breedInfo.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
                <div className="wtn-nbagr">
                  <span className="wtn-nbagr__mark">NBAGR</span>
                  <span>Certified Indigenous Breed · Govt. of India</span>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Grazing Map — Ghee only */}
      {farmer.grazingMap && (
        <>
          <WaveDivider height={72} palette={[PALETTE.CREAM_SOFT]} />
          <section className="wtn-section wtn-map-section">
            <div className="wtn-section__inner">
              <SectionHead
                eyebrow="Where They Graze"
                title="Alpine Meadows"
                sub="Nirmala's Badri cows graze freely on the alpine meadows surrounding Bhangeli — high-altitude pastures rich with wild medicinal herbs."
              />
              <div className="wtn-reveal">
                <MapEmbed label={farmer.grazingMap.label} url={farmer.grazingMap.url} />
              </div>
            </div>
          </section>
        </>
      )}

      {/* Farm Locations */}
      {farmer.farms && farmer.farms.length > 0 && (
        <>
          <WaveDivider height={72} palette={[PALETTE.CREAM, PALETTE.CREAM_SOFT]} flip />
          <section className="wtn-section wtn-farms">
            <div className="wtn-section__inner">
              <SectionHead
                eyebrow="Farm Locations"
                title="Walk the fields."
                sub="Satellite views of the exact plots this batch was grown on."
              />
              <div className="wtn-farms__grid wtn-reveal-group">
                {farmer.farms.map((farm, i) => (
                  <MapEmbed key={i} label={farm.label} url={farm.url} />
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Videos */}
      {farmer.videos && farmer.videos.length > 0 && (
        <>
          <WaveDivider height={72} palette={[PALETTE.CREAM_SOFT]} />
          <section className="wtn-section wtn-videos">
            <div className="wtn-section__inner">
              <SectionHead
                eyebrow="From the Field"
                title="Videos from the farm."
              />
              {farmer.videos.length > 1 && (
                <div className="wtn-videos__tabs wtn-reveal">
                  {farmer.videos.map((v, i) => (
                    <button
                      key={i}
                      className={`wtn-filter ${activeVideo === i ? 'is-active' : ''}`}
                      onClick={() => setActiveVideo(i)}
                    >
                      {v.label || `Clip ${i + 1}`}
                    </button>
                  ))}
                </div>
              )}
              <div className="wtn-videos__player wtn-reveal">
                {farmer.videos[activeVideo] && (
                  <>
                    <video
                      key={farmer.videos[activeVideo].url}
                      controls
                      className="wtn-videos__video"
                      playsInline
                    >
                      <source src={farmer.videos[activeVideo].url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    {farmer.videos.length === 1 && farmer.videos[0].label && (
                      <p className="wtn-videos__label">{farmer.videos[0].label}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Farming Calendar */}
      {farmer.calendar && farmer.calendar.length > 0 && (
        <>
          <WaveDivider height={72} palette={[PALETTE.CREAM, PALETTE.CREAM_SOFT]} flip />
          <section className="wtn-section wtn-calendar">
            <div className="wtn-section__inner">
              <SectionHead
                eyebrow="Farming Calendar"
                title="A year on the terrace."
              />
              <div className="wtn-calendar__timeline wtn-reveal-group">
                {farmer.calendar.map((item, i) => (
                  <div key={i} className="wtn-calendar__item">
                    <div className="wtn-calendar__icon">{item.icon}</div>
                    <div className="wtn-calendar__content">
                      <h4>{item.phase}</h4>
                      <p>{item.period}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Processing & Quality */}
      {farmer.processing && (
        <>
          <WaveDivider height={72} palette={[PALETTE.CREAM_SOFT]} />
          <section className="wtn-section wtn-processing">
            <div className="wtn-section__inner">
              <SectionHead
                eyebrow="Processing & Quality"
                title="Done by hand, done at home."
              />
              <div className="wtn-processing__tags wtn-reveal">
                {farmer.processing.practices.map((p, i) => (
                  <span key={i} className="wtn-btag wtn-btag--lg">{p}</span>
                ))}
              </div>
              <div className="wtn-processing__body wtn-reveal-group">
                {farmer.processing.description.split('\n\n').map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* Nutrition Facts Panel */}
      {farmer.nutritionFacts && (
        <>
          <WaveDivider height={72} palette={[PALETTE.CREAM, PALETTE.CREAM_SOFT]} flip />
          <section className="wtn-section wtn-nutrition">
            <div className="wtn-section__inner">
              <SectionHead
                eyebrow="Nutrition Profile"
                title={farmer.nutritionFacts.heading || 'Nutrition Facts'}
              />
              <div className="wtn-nf wtn-reveal">
                {farmer.nutritionFacts.servingsPerContainer && (
                  <p className="wtn-nf__servings">{farmer.nutritionFacts.servingsPerContainer}</p>
                )}
                {farmer.nutritionFacts.servingSize && (
                  <div className="wtn-nf__serving">
                    <span>Serving size</span>
                    <strong>{farmer.nutritionFacts.servingSize}</strong>
                  </div>
                )}
                <div className="wtn-nf__rule wtn-nf__rule--thick" />
                <p className="wtn-nf__per">{farmer.nutritionFacts.perLabel || 'Amount per serving'}</p>
                {farmer.nutritionFacts.calories !== undefined && (
                  <div className="wtn-nf__calories">
                    <span>Calories</span>
                    <strong>
                      {farmer.nutritionFacts.calories}
                      {farmer.nutritionFacts.caloriesUnit ? ` ${farmer.nutritionFacts.caloriesUnit}` : ''}
                    </strong>
                  </div>
                )}
                <div className="wtn-nf__rule" />
                <div className="wtn-nf__rda-header"><span>% RDA*</span></div>
                <ul className="wtn-nf__rows">
                  {farmer.nutritionFacts.rows.map((row, i) => {
                    if (row.type === 'header') {
                      return (
                        <li key={i} className="wtn-nf__section-header">{row.label}</li>
                      );
                    }
                    return (
                      <li key={i} className={`wtn-nf__item ${row.indent ? 'is-indent' : ''}`}>
                        <div className="wtn-nf__row">
                          <span className="wtn-nf__nutrient">{row.nutrient}</span>
                          <span className="wtn-nf__rda">{row.rda}</span>
                        </div>
                        {row.benefit && <p className="wtn-nf__benefit">{row.benefit}</p>}
                      </li>
                    );
                  })}
                </ul>
                <div className="wtn-nf__rule" />
                <p className="wtn-nf__footnote">{farmer.nutritionFacts.footnote}</p>
                {farmer.nutritionFacts.extraFootnote && (
                  <p className="wtn-nf__footnote wtn-nf__footnote--extra">{farmer.nutritionFacts.extraFootnote}</p>
                )}
                {farmer.nutritionFacts.basis && (
                  <p className="wtn-nf__footnote wtn-nf__footnote--basis">{farmer.nutritionFacts.basis}</p>
                )}
              </div>
            </div>
          </section>
        </>
      )}

      <WaveDivider height={72} palette={[PALETTE.CREAM_SOFT]} />

      {/* Traceability Info */}
      <section className="wtn-section wtn-trace">
        <div className="wtn-section__inner">
          <SectionHead
            eyebrow="Traceability Information"
            title="Every claim, on the record."
          />

          <div className="wtn-trace__grid wtn-reveal-group">

            {farmer.traceabilityWIP ? (
              <div className="wtn-trace__item wtn-trace__item--wip">
                <div className="wtn-trace__head">
                  <span className="wtn-trace__icon">🔄</span>
                  <div>
                    <h4>Traceability — In Progress</h4>
                    <p>Grazing logs, batch data, certification records</p>
                  </div>
                </div>
                <p className="wtn-trace__body">
                  We are currently building the full traceability layer for Badri Cow Ghee —
                  grazing logs, milk batch records, and certification documentation.
                  This page will be updated as our systems go live.
                </p>
                <p className="wtn-trace__body" style={{ marginTop: '10px' }}>
                  In the meantime, we share what we know: the breed, the meadow, the method,
                  and the nutrition profile above.
                </p>
              </div>
            ) : (
              <div className="wtn-trace__item">
                <div className="wtn-trace__head">
                  <span className="wtn-trace__icon">🚚</span>
                  <div>
                    <h4>Supply Chain Journey</h4>
                    <p>Processing, packaging, transport</p>
                  </div>
                </div>
                <div className="wtn-flow">
                  {supplyChain.map((step, i) => (
                    <div key={i} className="wtn-flow__wrap">
                      <div className="wtn-flow__step">
                        <div className="wtn-flow__icon">{step.icon}</div>
                        <div>
                          <h5>{step.title}</h5>
                          <p>{step.desc}</p>
                        </div>
                      </div>
                      {i < supplyChain.length - 1 && <div className="wtn-flow__arrow">↓</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="wtn-trace__item">
              <div className="wtn-trace__head">
                <span className="wtn-trace__icon">✅</span>
                <div>
                  <h4>Certification & Compliance</h4>
                  <p>Organic standards, audit logs</p>
                </div>
              </div>
              <p className="wtn-trace__body">
                Our base village, Bhangeli, is <strong>PSG certified</strong>. All sourcing
                follows documented organic protocols with periodic field audits.
              </p>
            </div>

            <div className="wtn-trace__item">
              <div className="wtn-trace__head">
                <span className="wtn-trace__icon">🌱</span>
                <div>
                  <h4>Sustainability Impact</h4>
                  <p>Environmental footprint, ethical sourcing</p>
                </div>
              </div>
              <p className="wtn-trace__body">
                We track and share batch-level sustainability metrics:
                {' '}<strong>carbon footprint</strong>, <strong>soil organic carbon (SOC)</strong>,
                water use efficiency, and fair-value sourcing. Every batch tells the full story.
              </p>
            </div>

          </div>
        </div>
      </section>

      <WaveDivider height={90} palette={[PALETTE.FOREST_SHADOW]} />

      {/* Footer */}
      <footer className="wtn-cv-footer">
        <div className="wtn-section__inner wtn-cv-footer__inner">
          <p className="wtn-cv-footer__batch">
            Batch ID · <strong>{data.batch_id}</strong>
          </p>
          <p className="wtn-cv-footer__brand">Orang Utan Organics</p>
          <p className="wtn-cv-footer__tagline">Himalayan Traceability</p>
          <div className="wtn-cv-footer__rule">
            <WaveRule width={140} stroke={PALETTE.MEADOW_GOLD} />
          </div>
          <p className="wtn-cv-footer__credit">
            Powered by <span className="wtn-cv-footer__credit-brand">TraceRoot</span>
          </p>
        </div>
      </footer>

    </div>
  );
}

export default CustomerView;
